import { useState } from 'react';
import {
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ClockIcon,
  XMarkIcon,
  GlobeAltIcon,
  SparklesIcon,
  NoSymbolIcon,
  CpuChipIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

interface Session {
  id: string;
  device: string;
  browser: string;
  location: string;
  ip: string;
  lastActive: string;
  isCurrent: boolean;
}

interface LoginHistory {
  id: string;
  timestamp: string;
  status: 'success' | 'failed';
  ip: string;
  location: string;
  device: string;
}

interface IPAllowlist {
  id: string;
  ip: string;
  description: string;
  added: string;
}

interface ContentFilter {
  id: string;
  type: 'prompt' | 'completion' | 'image';
  pattern: string;
  action: 'block' | 'flag';
  enabled: boolean;
}

interface ImageSafety {
  id: string;
  setting: string;
  description: string;
  enabled: boolean;
}

const mockSessions: Session[] = [
  {
    id: '1',
    device: 'MacBook Pro',
    browser: 'Chrome 120.0',
    location: 'San Francisco, CA',
    ip: '192.168.1.1',
    lastActive: '2024-01-20 15:30:00',
    isCurrent: true,
  },
  {
    id: '2',
    device: 'iPhone 15',
    browser: 'Safari Mobile 17.0',
    location: 'New York, NY',
    ip: '192.168.1.2',
    lastActive: '2024-01-19 10:15:00',
    isCurrent: false,
  },
];

const mockLoginHistory: LoginHistory[] = [
  {
    id: '1',
    timestamp: '2024-01-20 15:30:00',
    status: 'success',
    ip: '192.168.1.1',
    location: 'San Francisco, CA',
    device: 'MacBook Pro - Chrome',
  },
  {
    id: '2',
    timestamp: '2024-01-19 08:45:00',
    status: 'failed',
    ip: '192.168.1.3',
    location: 'Unknown Location',
    device: 'Unknown Device',
  },
];

const mockIPAllowlist: IPAllowlist[] = [
  {
    id: '1',
    ip: '192.168.1.0/24',
    description: 'Office Network',
    added: '2024-01-15',
  },
  {
    id: '2',
    ip: '10.0.0.1',
    description: 'Production Server',
    added: '2024-01-10',
  },
];

const mockContentFilters: ContentFilter[] = [
  {
    id: '1',
    type: 'prompt',
    pattern: '(password|credit.?card|ssn)',
    action: 'block',
    enabled: true,
  },
  {
    id: '2',
    type: 'completion',
    pattern: '(harmful|malicious|illegal)',
    action: 'flag',
    enabled: true,
  },
  {
    id: '3',
    type: 'image',
    pattern: '(nsfw|violence|gore)',
    action: 'block',
    enabled: true,
  },
];

const mockImageSafety: ImageSafety[] = [
  {
    id: '1',
    setting: 'Content Safety',
    description: 'Block generation of inappropriate or unsafe content',
    enabled: true,
  },
  {
    id: '2',
    setting: 'Watermarking',
    description: 'Add digital watermarks to generated images',
    enabled: true,
  },
  {
    id: '3',
    setting: 'Metadata Tracking',
    description: 'Track image generation metadata and provenance',
    enabled: true,
  },
];

export default function Security() {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [sessions, setSessions] = useState<Session[]>(mockSessions);
  const [loginHistory] = useState<LoginHistory[]>(mockLoginHistory);
  const [ipAllowlist, setIPAllowlist] = useState<IPAllowlist[]>(mockIPAllowlist);
  const [contentFilters, setContentFilters] = useState<ContentFilter[]>(mockContentFilters);
  const [imageSafety, setImageSafety] = useState<ImageSafety[]>(mockImageSafety);
  const [modelRestrictions, setModelRestrictions] = useState({
    gpt4: true,
    gpt35turbo: true,
    claude2: false,
    dalle3: true,
    dalle2: true,
  });

  const terminateSession = (sessionId: string) => {
    setSessions(sessions.filter(session => session.id !== sessionId));
  };

  const terminateAllSessions = () => {
    setSessions(sessions.filter(session => session.isCurrent));
  };

  const removeFromAllowlist = (id: string) => {
    setIPAllowlist(ipAllowlist.filter(ip => ip.id !== id));
  };

  const toggleFilter = (id: string) => {
    setContentFilters(
      contentFilters.map(filter =>
        filter.id === id ? { ...filter, enabled: !filter.enabled } : filter
      )
    );
  };

  const toggleImageSafety = (id: string) => {
    setImageSafety(
      imageSafety.map(setting =>
        setting.id === id ? { ...setting, enabled: !setting.enabled } : setting
      )
    );
  };

  return (
    <div className="space-y-8">
      {/* Security Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <ShieldCheckIcon className="h-6 w-6 text-white/90" />
          <h1 className="text-xl font-medium text-white">Security Settings</h1>
        </div>

        {/* Two-Factor Authentication */}
        <div className="p-8">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <h2 className="text-lg font-medium text-white">
                Two-Factor Authentication (2FA)
              </h2>
              <p className="text-sm text-white/50">
                Add an extra layer of security to your account by requiring both a password
                and a verification code from your mobile device.
              </p>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  twoFactorEnabled ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    twoFactorEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Model Access Controls */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <CpuChipIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Model Access Controls</h2>
        </div>
        <div className="p-8">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-white">GPT-4</h3>
                <p className="text-sm text-white/50">
                  Most capable text model, best for complex tasks
                </p>
              </div>
              <button
                onClick={() => setModelRestrictions(prev => ({ ...prev, gpt4: !prev.gpt4 }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  modelRestrictions.gpt4 ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    modelRestrictions.gpt4 ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-white">GPT-3.5 Turbo</h3>
                <p className="text-sm text-white/50">
                  Fast and cost-effective for most text tasks
                </p>
              </div>
              <button
                onClick={() => setModelRestrictions(prev => ({ ...prev, gpt35turbo: !prev.gpt35turbo }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  modelRestrictions.gpt35turbo ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    modelRestrictions.gpt35turbo ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-white">Claude 2</h3>
                <p className="text-sm text-white/50">
                  Alternative text model with different capabilities
                </p>
              </div>
              <button
                onClick={() => setModelRestrictions(prev => ({ ...prev, claude2: !prev.claude2 }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  modelRestrictions.claude2 ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    modelRestrictions.claude2 ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-white">DALL-E 3</h3>
                <p className="text-sm text-white/50">
                  Latest image generation model with high quality
                </p>
              </div>
              <button
                onClick={() => setModelRestrictions(prev => ({ ...prev, dalle3: !prev.dalle3 }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  modelRestrictions.dalle3 ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    modelRestrictions.dalle3 ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-white">DALL-E 2</h3>
                <p className="text-sm text-white/50">
                  Cost-effective image generation model
                </p>
              </div>
              <button
                onClick={() => setModelRestrictions(prev => ({ ...prev, dalle2: !prev.dalle2 }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  modelRestrictions.dalle2 ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    modelRestrictions.dalle2 ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* IP Allowlist */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <GlobeAltIcon className="h-6 w-6 text-white/90" />
            <h2 className="text-lg font-medium text-white">IP Allowlist</h2>
          </div>
          <button className="btn-secondary text-sm">Add IP Address</button>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {ipAllowlist.map((ip) => (
            <div
              key={ip.id}
              className="px-8 py-4 flex items-center justify-between"
            >
              <div>
                <div className="text-sm font-medium text-white">{ip.ip}</div>
                <div className="text-sm text-white/50">{ip.description}</div>
                <div className="text-xs text-white/30">Added: {ip.added}</div>
              </div>
              <button
                onClick={() => removeFromAllowlist(ip.id)}
                className="p-2 text-white/50 hover:text-rose-500 hover:bg-white/[0.05] rounded transition-colors duration-200"
                title="Remove IP"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Content Filtering */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <NoSymbolIcon className="h-6 w-6 text-white/90" />
            <h2 className="text-lg font-medium text-white">Content Filtering</h2>
          </div>
          <button className="btn-secondary text-sm">Add Filter</button>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {contentFilters.map((filter) => (
            <div
              key={filter.id}
              className="px-8 py-4 flex items-center justify-between"
            >
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-white capitalize">
                    {filter.type} Filter
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      filter.action === 'block'
                        ? 'bg-rose-500/10 text-rose-300'
                        : 'bg-amber-500/10 text-amber-300'
                    }`}
                  >
                    {filter.action}
                  </span>
                </div>
                <div className="text-sm text-white/50">
                  Pattern: <code className="bg-white/[0.05] px-1 rounded">{filter.pattern}</code>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => toggleFilter(filter.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    filter.enabled ? 'bg-magick-purple' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      filter.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <button
                  onClick={() => {/* TODO: Implement edit filter */}}
                  className="p-2 text-white/50 hover:text-white hover:bg-white/[0.05] rounded transition-colors duration-200"
                >
                  <SparklesIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image Safety */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <PhotoIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Image Safety Controls</h2>
        </div>
        <div className="p-8 space-y-4">
          {imageSafety.map((setting) => (
            <div
              key={setting.id}
              className="flex items-center justify-between p-4 glass-panel-dark"
            >
              <div>
                <div className="font-medium text-white">{setting.setting}</div>
                <div className="text-sm text-white/50">{setting.description}</div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => toggleImageSafety(setting.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    setting.enabled ? 'bg-magick-purple' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      setting.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Sessions */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ComputerDesktopIcon className="h-6 w-6 text-white/90" />
            <h2 className="text-lg font-medium text-white">Active Sessions</h2>
          </div>
          {sessions.length > 1 && (
            <button
              onClick={terminateAllSessions}
              className="btn-secondary text-sm"
            >
              Terminate All Other Sessions
            </button>
          )}
        </div>
        <div className="divide-y divide-white/[0.05]">
          {sessions.map((session) => (
            <div
              key={session.id}
              className="px-8 py-4 flex items-center justify-between"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-white/[0.05] p-2 rounded">
                  {session.device.toLowerCase().includes('iphone') ? (
                    <DevicePhoneMobileIcon className="h-5 w-5 text-white/90" />
                  ) : (
                    <ComputerDesktopIcon className="h-5 w-5 text-white/90" />
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-white flex items-center space-x-2">
                    <span>{session.device}</span>
                    {session.isCurrent && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-magick-purple/20 text-magick-purple">
                        Current Session
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white/50">
                    {session.browser} • {session.location}
                  </div>
                  <div className="text-xs text-white/30">
                    Last active: {session.lastActive}
                  </div>
                </div>
              </div>
              {!session.isCurrent && (
                <button
                  onClick={() => terminateSession(session.id)}
                  className="p-2 text-white/50 hover:text-rose-500 hover:bg-white/[0.05] rounded transition-colors duration-200"
                  title="Terminate Session"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Login History */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <ClockIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Login History</h2>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {loginHistory.map((login) => (
            <div key={login.id} className="px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className={`h-2 w-2 rounded-full ${
                      login.status === 'success'
                        ? 'bg-emerald-500'
                        : 'bg-rose-500'
                    }`}
                  />
                  <div>
                    <div className="text-sm font-medium text-white">
                      {login.device}
                    </div>
                    <div className="text-sm text-white/50">
                      {login.location} • {login.ip}
                    </div>
                    <div className="text-xs text-white/30">
                      {login.timestamp}
                    </div>
                  </div>
                </div>
                <span
                  className={`text-sm px-2 py-1 rounded-full ${
                    login.status === 'success'
                      ? 'bg-emerald-500/10 text-emerald-300'
                      : 'bg-rose-500/10 text-rose-300'
                  }`}
                >
                  {login.status === 'success' ? 'Successful' : 'Failed'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
